import { RosterIcon } from "@xola/icons";
import { flash, Spinner, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { SecondaryButton } from "common";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Event,
    Experience,
    ExperiencePurchaseItem,
    LineItem,
    MerchandisePurchaseItem,
    OPEN_UNLIMITED,
    Permission,
    PurchasedUnit,
    Timeslot,
} from "types";
import { CapacitySpinner } from "../../Dashboard/Roster/CapacitySpinner";
import { CopyableField } from "../CopyableField";
import { IsGranted } from "../IsGranted";
import styles from "./Banner.module.css";
import { BannerArrival } from "./BannerArrival";
import { BannerAvailability } from "./BannerAvailability";
import { BannerBadges } from "./BannerBadges";
import { BannerDataProps } from "./BannerDataProps";
import { BannerDemographicList } from "./BannerDemographicList";
import { DashboardRosterDrawer, useTimeSlotFromItem } from "@/components/PurchasesMenu/DashboardRosterDrawer";
import { useResource } from "@/hooks/useResource";
import { useVisibility } from "@/hooks/useVisibility";
import { isScheduledItem } from "@/models/PurchaseItem";
import { usePurchaseDrawerStore } from "@/stores/purchaseDrawerStore";
import { getSeller } from "@/utils/getSeller";

export interface BannerProps {
    event?: Event;
    timeslot?: Timeslot;
    bannerData: BannerDataProps;
    className?: string;
    style?: any;
    url: string;
    demographics?: LineItem[] | PurchasedUnit[];
    experience?: Experience;
    item: ExperiencePurchaseItem | MerchandisePurchaseItem;
}

export const Banner = ({
    event,
    timeslot,
    bannerData,
    url,
    demographics,
    className,
    style,
    experience,
    item,
}: BannerProps) => {
    const hasEvent = item.event;
    const [disableRosterButton, setDisableRosterButton] = useState({ reason: "", isDisable: false });
    const history = useHistory();
    const [visible, { toggle, hide }] = useVisibility(false);

    const isPurchasePage = useMemo(() => history.location.pathname.startsWith("/purchases"), [history]);
    const isPurchaseDrawerOpen = usePurchaseDrawerStore((state) => Boolean(state.purchaseId ?? state.purchaseItemId));
    const isScheduled = isScheduledItem(item);
    const { timeslot: timeslotFromItem, isLoading } = useTimeSlotFromItem(item, visible);
    const { data: itemData, isLoading: isLoadingItem } = useResource<ExperiencePurchaseItem>(
        ["purchaseItems", item.id],
        null,
        {
            enabled: !hasEvent,
        },
    );
    const purchaseItem = hasEvent ? item : itemData;
    const isNotTimeslotExist = !timeslot && !timeslotFromItem;
    const isLoadingExperiencesOrPurchases = isLoading || isLoadingItem;

    useEffect(() => {
        if (isNotTimeslotExist) {
            // A reservation being moved re-keys the timeslot lookup, so skip the miss while the purchase
            // drawer is open rather than hiding the roster underneath it and flashing a false error.
            if (!(isLoadingExperiencesOrPurchases || !purchaseItem) && visible && !isPurchaseDrawerOpen) {
                hide();
                flash.danger("No roster exists for this reservation");
            }

            setDisableRosterButton({ reason: "No roster exists for this reservation", isDisable: true });
        }
    }, [
        visible,
        isNotTimeslotExist,
        toggle,
        isLoadingExperiencesOrPurchases,
        purchaseItem,
        hide,
        isPurchaseDrawerOpen,
    ]);

    useEffect(() => {
        setDisableRosterButton({ reason: "", isDisable: isLoadingExperiencesOrPurchases || !purchaseItem });
    }, [isLoadingExperiencesOrPurchases, purchaseItem]);

    return (
        <>
            <div
                className={clsx(
                    "ui-product-banner flex h-40 min-h-[160px] justify-between rounded-lg p-6 text-white",
                    styles.root,
                    className,
                )}
                style={{ ...style, "--background": `url(${url})` }}
            >
                <div className="flex flex-col justify-center">
                    <div className="no-translate text-xl font-bold" data-testid="banner-product-name">
                        {bannerData.title}
                    </div>
                    <div className="space-y-4">
                        <BannerArrival {...bannerData} event={event} experience={experience} />
                        <BannerBadges {...bannerData} event={event} />
                    </div>
                </div>

                <div className="relative flex w-2/5 flex-col justify-between">
                    <div className="flex items-center justify-end">
                        <IsGranted permissions={Permission.DASHBOARD_VIEW}>
                            {isPurchasePage && isScheduled && item.seller?.id === getSeller().id && (
                                <Tooltip
                                    content={
                                        disableRosterButton.isDisable && disableRosterButton.reason
                                            ? disableRosterButton.reason
                                            : "Open Roster"
                                    }
                                >
                                    <SecondaryButton
                                        id="roster_drawer_button"
                                        size="small"
                                        disabled={disableRosterButton.isDisable}
                                        onClick={toggle}
                                    >
                                        {isLoading ? <Spinner size="tiny" /> : <RosterIcon color="black" />}
                                    </SecondaryButton>
                                </Tooltip>
                            )}
                        </IsGranted>
                    </div>

                    {!isPurchasePage && timeslot && timeslot.capacity.max < OPEN_UNLIMITED ? (
                        <div className="mb-3 flex items-center justify-end">
                            <CapacitySpinner key={`${timeslot.id}-${event?.id}`} event={event} timeslot={timeslot} />
                        </div>
                    ) : null}

                    {!isPurchasePage && timeslot && <BannerAvailability timeslot={timeslot} bannerData={bannerData} />}
                    {experience && demographics && (
                        <div className="flex justify-end space-x-2" data-testid="banner-demographics">
                            <BannerDemographicList demographics={demographics} experience={experience} />
                        </div>
                    )}
                    <div className="flex justify-end space-x-2">
                        <CopyableField
                            data={bannerData.shortCode}
                            classNames={{ badge: "m-auto" }}
                            size="medium"
                            toolTipPlacement="bottom"
                            dataTestId="purchase-customer-information-shortcode"
                        >
                            <u className="mr-1">ID:</u>
                            {bannerData.shortCode}
                        </CopyableField>
                    </div>
                </div>
            </div>

            <DashboardRosterDrawer
                isOpen={visible && !isLoading && (!!timeslot || !!timeslotFromItem)}
                timeslot={timeslot ?? timeslotFromItem}
                onClose={toggle}
            />
        </>
    );
};
